/**
 * LA TAXONOMIE D'ÉVÉNEMENTS — une liste, et une seule (ROADMAP 9.1, §11).
 *
 * ══ Pourquoi une liste typée plutôt que des chaînes libres ══════════════════
 *
 * Un nom d'événement mal orthographié ne casse rien : il crée une SECONDE série, vide,
 * à côté de la vraie. On ne s'en aperçoit qu'en regardant un entonnoir qui n'a jamais
 * décollé — des semaines plus tard, et sans jamais soupçonner la faute de frappe. C'est
 * le défaut le plus courant d'un plan de mesure, et le seul qu'un type peut supprimer.
 *
 * ⚠️ **Un événement qui ne répond à aucune question n'existe pas.** Chacun porte ici la
 * question qu'il sert : sans elle, on collecte par réflexe, on accumule du bruit, et on
 * finit par ne plus rien lire du tout. Si une question disparaît, l'événement part avec.
 *
 * ══ Ce qu'on ne mesure PAS, délibérément ════════════════════════════════════
 *
 * Pas d'autocapture (chaque clic de l'interface), pas d'enregistrement de session, pas de
 * frappe au clavier. Le §11 veut un mode sans cookie et sans bannière : la contrepartie
 * est qu'on ne collecte QUE ce qu'on a décidé de collecter, et qu'on le nomme.
 */

/**
 * Les événements du jeu. La valeur est le nom envoyé à PostHog.
 *
 * Convention : `objet_verbe_au_passé`, en anglais — c'est ce que lisent les tableaux de
 * PostHog, et mélanger deux langues dans une liste d'événements rend le tri illisible.
 */
export const EVENTS = {
  /* ── L'ENTONNOIR D'ENTRÉE ─────────────────────────────────────────────────
     La question : combien de gens qui ouvrent le jeu posent un bâtiment, et
     combien reviennent ? C'est LA mesure qui dit si les dix premières secondes
     (E§2) tiennent leur promesse. */

  /** Le jeu est jouable à l'écran. Le dénominateur de tout le reste. */
  gameStarted: 'game_started',
  /** Premier bâtiment posé, jamais les suivants — c'est un jalon, pas un compteur. */
  firstBuildingPlaced: 'first_building_placed',
  /** Premier terrain acheté : le joueur a compris qu'il pouvait s'étendre. */
  firstLandBought: 'first_land_bought',
  /** Première ère ouverte : il a compris la boucle, et il y revient. */
  firstPrestige: 'first_prestige',

  /* ── LA BOUCLE VIRALE (§9) ────────────────────────────────────────────────
     La question : un partage amène-t-il des visiteurs, et un visiteur devient-il
     joueur ? Sans ces trois-là, on ne sait pas si le cœur du jeu fonctionne. */

  /** Le joueur a partagé son lien — le geste, pas l'intention. */
  cityShared: 'city_shared',
  /** Une page publique a été VUE. Émis depuis `/ville/:slug`, pas depuis le jeu. */
  cityVisited: 'city_visited',
  /** Un visiteur a cliqué « Crée ta ville » : la conversion, mesurée à sa source. */
  visitConverted: 'visit_converted',

  /* ── L'ARGENT (§14, G§1) ──────────────────────────────────────────────────
     La question : à quel moment un joueur décide de payer, et lesquels
     abandonnent en route ? L'entonnoir compte plus que le total. */

  /** La boutique de gemmes s'ouvre — et POURQUOI elle s'ouvre (`reason`). */
  storeOpened: 'store_opened',
  /** Un pack est choisi : le dernier geste avant de quitter le jeu pour payer. */
  checkoutStarted: 'checkout_started',
  /** Le paiement est revenu confirmé. L'écart avec le précédent EST l'abandon. */
  checkoutCompleted: 'checkout_completed',
  /** Des gemmes sont dépensées, avec l'offre choisie : ce qui se vend vraiment. */
  gemsSpent: 'gems_spent',

  /* ── CE QUI COINCE ────────────────────────────────────────────────────────
     La question : où le joueur se heurte-t-il à un mur ? Un refus répété est un
     défaut de conception, pas un joueur maladroit. */

  /** Une action refusée, avec son motif. Le motif est tout l'intérêt. */
  actionRefused: 'action_refused',
  /** Le tutoriel est abandonné, et à quelle étape. */
  tutorialSkipped: 'tutorial_skipped',

  /* ── HYGIÈNE DE DONNÉES ───────────────────────────────────────────────────
     La question : le job de purge fait-il ce qu'on croit ? CLAUDE.md l'exige
     nommément — « chaque purge émet un événement `city_purged`, pour qu'une
     dérive du job soit visible dans les métriques au lieu d'être découverte par
     un joueur qui a perdu sa ville ». */
  cityPurged: 'city_purged',
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

/**
 * Propriétés admises sur un événement.
 *
 * Volontairement pauvre : ni chaîne libre saisie par le joueur, ni identifiant d'une
 * autre personne, ni contenu de ville. Une propriété qu'on ne saurait pas justifier
 * devant quelqu'un qui demande « pourquoi vous stockez ça » n'a rien à faire ici.
 */
export type EventProps = Readonly<Record<string, string | number | boolean>>;
